/**
 * Dashboard (docs/05 route /). Sections fill in as their modules land.
 *
 * The banner at the top is punch 4.6 (docs/11 D-5): the calm half of v1's
 * `PortfolioHeroBanner`, greeting and date only. It states nothing it has not
 * read — the numbers live in the Action items card below, which has a query
 * behind it.
 *
 * "Needs attention" is now REAL (Matt 2026-08-25). It had been a NotYetState
 * promising "expiring leases, agreements, COIs" — the first screen every user
 * sees, describing a feature that did not exist. It now shows the nearest few
 * dates from the portfolio expirations read, lapsed first, and links to the
 * lanes for the rest.
 *
 * "My work" is still a NotYetState, and the distinction is the point: there is
 * no tasks table, so a card claiming "nothing assigned to you" would be
 * asserting the one thing it cannot know (O-28). An empty state says "we looked
 * and there is nothing"; this screen has not looked.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router';

import type { Me } from '../../app/api.js';
import { Card, CardHeader, NotYetState } from '../../components/ui/index.js';
import { ExpirationList } from '../expirations/ExpirationList.js';

import { DashboardBanner } from './DashboardBanner.js';

export function DashboardScreen({ me }: { readonly me: Me }): ReactNode {
  // The card is a summary, not the list: six rows is a glance, and the lanes
  // are one click away for the work itself.
  const canSeeDates = me.permissions.includes('tasks.view');
  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4">
      {/* Punch 4.6 — the banner CARRIES the page's h1. It replaced a bare
          "Good day" heading rather than being added above one, so there is
          still exactly one h1 on the dashboard. */}
      <DashboardBanner displayName={me.displayName} />
      <div className="grid gap-4 lg:grid-cols-2">
        {/* ADR-50 renamed "Needs attention" to the one word for dated work.
            The card ABOVE keeps "My work" deliberately: it is a not-yet surface
            for assigned TASKS, which is a different idea from a dated
            obligation and a module that has not landed. Giving both cards the
            same title would be the "five names for one idea" problem inverted —
            one name for two ideas — so the merge is left to the Action-items
            phase 2 that rebuilds this area (docs/11 F-3). */}
        <Card>
          <CardHeader title="My work" />
          <NotYetState body="Tasks assigned to you will appear here once the Work module lands." />
        </Card>
        <Card>
          <CardHeader
            title="Action items"
            action={
              canSeeDates ? (
                <Link
                  to="/work/due-soon"
                  className="text-meta text-brand-700 no-underline hover:underline"
                >
                  See all
                </Link>
              ) : undefined
            }
          />
          {canSeeDates ? (
            <ExpirationList
              params={{ scope: 'mine', lane: 'all', limit: 6 }}
              emptyTitle="Nothing needs attention"
              emptyBody="No lease, agreement, certificate or permit is lapsed or inside its renewal window."
            />
          ) : (
            <NotYetState body="Expiring leases, agreements and certificates appear here for users who can view work." />
          )}
        </Card>
      </div>
    </div>
  );
}
